import { ArrowRight } from 'lucide-react'
import moment from 'moment'
import { parseAsString, useQueryState } from 'nuqs'
import { ReactNode } from 'react'
import { Link } from 'react-router'
import useSWR from 'swr'

import { CashRegisterNav } from '@/components/CashRegister/CashRegisterNav'
import { DatePaginator } from '@/components/DatePaginator/DatePaginator'
import { Money, PageHeader, StatStrip } from '@/components/ds'
import { EetWarningBanner } from '@/components/Eet/EetWarningBanner'
import { MonthPaginator } from '@/components/MonthPaginator/MonthPaginator'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { Settings } from '@/interfaces/Settings'
import { Summary, SummaryRow } from '@invo.space/shared'
import { range } from '@/lib/array'

const MONTHS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec']

const TaxFreeHint = ({ row }: { row: SummaryRow }) => (
  <span className="inline-flex flex-wrap items-baseline gap-x-1">
    bez DPH <Money amount={row.totalTaxFree.amount} currency={row.totalTaxFree.currency} size="sm" className="font-normal" />
  </span>
)

/** Souhrn za období: pruh s tržbou a platbami, pod tím rozpad podle sazeb DPH. */
const SummaryBlock = ({ title, controls, below, summary, isLoading, action }: { title: string; controls?: ReactNode; below?: ReactNode; summary?: Summary; isLoading: boolean; action?: ReactNode }) => {
  const types: { key: keyof Summary['byType']; label: string }[] = [
    { key: 'Cash', label: 'Hotově' },
    { key: 'Card', label: 'Kartou' },
    { key: 'Qr', label: 'QR kód' },
  ]
  const taxRows = Object.entries(summary?.byTax ?? {}).sort(([a], [b]) => Number(b) - Number(a))

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-medium">{title}</h2>
        <div className="flex flex-wrap items-center gap-2">
          {controls}
          {action}
        </div>
      </div>
      {below}

      {isLoading && <Skeleton className="h-[92px] rounded-lg" />}

      {summary && (
        <>
          <StatStrip
            items={[
              {
                label: 'Tržba celkem',
                value: <Money amount={summary.total.total.amount} currency={summary.total.total.currency} size="lg" />,
                hint: <TaxFreeHint row={summary.total} />,
              },
              ...types.map(({ key, label }) => ({
                label,
                value: <Money amount={summary.byType[key]?.total.amount ?? 0} currency={summary.byType[key]?.total.currency ?? summary.total.total.currency} size="lg" />,
                hint: summary.byType[key] ? <TaxFreeHint row={summary.byType[key]} /> : undefined,
              })),
            ]}
          />

          {taxRows.length > 0 && (
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Sazba DPH</TableHead>
                    <TableHead className="hidden text-right md:table-cell">Základ</TableHead>
                    <TableHead className="hidden text-right md:table-cell">DPH</TableHead>
                    <TableHead className="text-right">Celkem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taxRows.map(([rate, row]) => (
                    <TableRow key={rate} className="hover:bg-transparent">
                      <TableCell className="tabular">{rate && rate !== '0' ? `${rate} %` : 'Bez DPH'}</TableCell>
                      <TableCell className="hidden text-right md:table-cell"><Money amount={row.totalTaxFree.amount} currency={row.totalTaxFree.currency} /></TableCell>
                      <TableCell className="hidden text-right md:table-cell"><Money amount={row.totalTax.amount} currency={row.totalTax.currency} /></TableCell>
                      <TableCell className="text-right">
                        <Money amount={row.total.amount} currency={row.total.currency} />
                        <p className="text-xs text-muted-foreground md:hidden">
                          základ <Money amount={row.totalTaxFree.amount} currency={row.totalTaxFree.currency} size="sm" className="font-normal text-muted-foreground" />
                          , DPH <Money amount={row.totalTax.amount} currency={row.totalTax.currency} size="sm" className="font-normal text-muted-foreground" />
                        </p>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow className="hover:bg-transparent">
                    <TableCell className="font-medium">Celkem</TableCell>
                    <TableCell className="hidden text-right md:table-cell"><Money amount={summary.total.totalTaxFree.amount} currency={summary.total.totalTaxFree.currency} /></TableCell>
                    <TableCell className="hidden text-right md:table-cell"><Money amount={summary.total.totalTax.amount} currency={summary.total.totalTax.currency} /></TableCell>
                    <TableCell className="text-right">
                      <Money amount={summary.total.total.amount} currency={summary.total.total.currency} />
                      <p className="text-xs font-normal text-muted-foreground md:hidden">
                        základ <Money amount={summary.total.totalTaxFree.amount} currency={summary.total.totalTaxFree.currency} size="sm" className="font-normal text-muted-foreground" />
                        , DPH <Money amount={summary.total.totalTax.amount} currency={summary.total.totalTax.currency} size="sm" className="font-normal text-muted-foreground" />
                      </p>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </>
      )}
    </section>
  )
}

export default function CashRegisterDashboardPage() {
  const [date] = useQueryState('date', parseAsString.withDefault(moment().format('YYYY-MM-DD')))
  const [year] = useQueryState('year', parseAsString.withDefault(moment().year().toString()))
  const [month] = useQueryState('month', parseAsString.withDefault((moment().month() + 1).toString()))

  const { data: settings } = useSWR<Settings>(`/api/v2/settings`, apiFetcher)
  const { data: dailySummary, isLoading: isDailySummaryLoading } = useSWR<Summary>(`/api/v1/cash-register/daily-summary?date=${date}`, apiFetcher)
  const { data: monthlySummary, isLoading: isMonthlySummaryLoading } = useSWR<Summary>(
    `/api/v1/cash-register/monthly-summary?year=${year}&month=${month}`,
    apiFetcher,
  )

  useSetPageHeader({
    breadcrumbs: [
      { href: '/cash-register', children: 'Pokladna' },
      { href: '/cash-register/dashboard', children: 'Přehledy' },
    ],
    newButton: 'invoice',
  })

  return (
    <>
      <title>Přehledy pokladny</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <EetWarningBanner />
        <CashRegisterNav className="mb-5" />

        <PageHeader title="Přehledy" description="Tržby z pokladny za den a za měsíc podle způsobu platby a sazby DPH." />

        <div className="space-y-10">
          <SummaryBlock
            title={`Den ${moment(date).format('D. M. YYYY')}`}
            controls={<DatePaginator />}
            action={
              <Button variant="ghost" asChild>
                <Link to={`/cash-register/receipts?date=${date}`}>
                  Účtenky dne
                  <ArrowRight />
                </Link>
              </Button>
            }
            summary={dailySummary}
            isLoading={isDailySummaryLoading}
          />

          <SummaryBlock
            title={`Měsíc ${MONTHS[Number(month) - 1] ?? ''} ${year}`}
            below={<MonthPaginator allowAllMonths={false} years={range(settings?.yearOfRegistration || 2022, new Date().getFullYear())} />}
            summary={monthlySummary}
            isLoading={isMonthlySummaryLoading}
          />
        </div>
      </div>
    </>
  )
}
